import { toast } from 'sonner';
import { useSmallAppEditorStore } from '@/stores/smallapp-editor-store';
import { useModerationDialogStore } from '@/features/smallapp/moderation/stores/moderation-dialog-store';
import type { ModerationAsset } from '@/features/smallapp/moderation/types';
import { registerActionHandler } from '../action-registry';

/**
 * `openModerationDialog` — opens the native Moderation dialog for the current asset.
 *
 * scio1 mirror: `instagramLoginModal.html:200-211` cog button next to "Always show
 * new posts" calls `openModeration()` → `ModerationModal`.
 *
 * Reads the current asset from `useSmallAppEditorStore` (the form has no `_id`) and
 * opens via `useModerationDialogStore.open()`. The manifest declares `disabled: 'add'`
 * so the button only enables on saved assets; the missing-asset toast guards races
 * (e.g. playground with no asset bound).
 */
registerActionHandler('openModerationDialog', async ({ appMeta, t }) => {
  const asset = useSmallAppEditorStore.getState().asset;
  if (!asset?._id) {
    toast.error(t('Save the asset before opening moderation'));
    return;
  }

  const appType = appMeta?.appType ?? asset.appType ?? '';
  if (!appType) {
    toast.error(t('Asset is missing appType — cannot open moderation'));
    return;
  }

  // Strict gate — moderation REST URLs are built off `AppMeta.apiUrl`. Without
  // it we have no base path to query the smallapp microservice. AppMetas in
  // sandbox sometimes ship without `apiUrl` for apps that haven't been wired
  // through the manifest engine yet.
  const apiUrl = appMeta?.apiUrl;
  if (!apiUrl) {
    toast.error(t('This app does not expose a moderation API'));
    return;
  }

  const moderationAsset: ModerationAsset = { ...asset, appType } as ModerationAsset;

  useModerationDialogStore.getState().open({
    asset: moderationAsset,
    appType,
    apiUrl,
    // Dialog returns `{ sortBy }` on close; the manifest doesn't expose sortBy as a
    // form field, so we drop the result — the next open re-reads from persistence.
    resolve: () => undefined,
  });
});
